import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FormField, SurveyResponseData } from '../features/surveys/types';
import { SurveyResponsesTable } from '../components/SurveyResponsesTable';
import { ExportToExcel, ExcelColumn } from '../components/ExportToExcel';
import {
  loadSurveyResponses,
  clearResponses,
} from '../features/surveys/surveyResponsesSlice';
import { loadSurveyById, clearCurrentSurvey } from '../features/surveys/surveysSlice';
import { loadSurveyForms } from '../features/surveys/surveyFormsSlice';
import { selectCurrentUser } from '../features/auth/authSlice';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import {
  detectCategoryFromPath,
  getAssessmentListLabel,
  getAssessmentListPath,
  getAssessmentResponsesTitle,
  getAssessmentSingularLabel,
} from '../features/surveys/assessmentLabels';

const QUALITATIVE_MAP: Record<string, number> = {
  'excelente': 5,
  'muy bueno': 4,
  'muy buena': 4,
  'bueno': 3,
  'buena': 3,
  'regular': 2,
  'malo': 1,
  'mala': 1,
  'muy malo': 1,
  'muy mala': 1,
  'si': 5,
  'sí': 5,
  'no': 1,
};

function mapToNumeric(value: any): number | string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    return mapStringToNumeric(value);
  }

  if (typeof value === 'boolean') return value ? 5 : 1;
  return '';
}

function mapStringToNumeric(value: string): number | string {
  const normalized = value.toLowerCase().trim();
  const numericValue = Number(normalized);
  if (!isNaN(numericValue)) return numericValue;

  const mappedValue = QUALITATIVE_MAP[normalized];
  if (mappedValue !== undefined) return mappedValue;
  return '';
}

function computeMultipleCheckboxAverage(values: any[]): number | string {
  const numericValues = values
    .map(v => mapToNumeric(v))
    .filter((v): v is number => typeof v === 'number');
  if (numericValues.length === 0) return '';
  return numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
}

function mapOptionFieldValue(field: FormField, value: any): number | string {
  if (field.type === 'checkbox-multiple' && Array.isArray(value)) {
    return computeMultipleCheckboxAverage(value);
  }
  const numericValue = mapToNumeric(value);
  return typeof numericValue === 'number' ? numericValue : '';
}

function mapFieldValueForExport(field: FormField, value: any): number | string {
  if (value === null || value === undefined || value === '') return '';

  const hasOptions = field.options && field.options.length > 0;
  if (hasOptions) return mapOptionFieldValue(field, value);
  if (field.type === 'checkbox') return mapToNumeric(value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function buildFieldColumn(field: FormField): ExcelColumn<SurveyResponseData> {
  return {
    header: field.label,
    accessor: (row: SurveyResponseData) => mapFieldValueForExport(field, row.data[field.id]),
  };
}

function buildTotalScoreColumn(): ExcelColumn<SurveyResponseData> {
  return {
    header: 'Puntaje Total Ponderado',
    accessor: (row) => row.calculation ? row.calculation.totalScore.toFixed(2) : 'N/A',
  };
}

export function SurveyResponsesPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const category = detectCategoryFromPath(location.pathname);
  const listPath = getAssessmentListPath(category);
  const listLabel = getAssessmentListLabel(category);
  const singularLabel = getAssessmentSingularLabel(category);
  const { items, loading, filters } = useSelector(
    (state: RootState) => state.surveyResponses
  );
  const { currentSurvey } = useSelector((state: RootState) => state.surveys);
  const { items: forms } = useSelector((state: RootState) => state.surveyForms);
  const currentUser = useSelector(selectCurrentUser);
  const canExportExcel = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [belowThresholdOnly, setBelowThresholdOnly] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(loadSurveyById(id));
      dispatch(loadSurveyForms(id));
      // Cargar todas las respuestas sin filtrar por revisión para determinar qué revisiones tienen respuestas
      dispatch(loadSurveyResponses({ surveyId: id, filters: { ...filters, formId: undefined }, fetchAll: true }));
    }

    return () => {
      dispatch(clearResponses());
      dispatch(clearCurrentSurvey());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarga atada a id y sortDir (comportamiento preexistente)
  }, [dispatch, id, filters.sortDir]);

  // Obtener revisiones con respuestas
  const formsWithResponses = useMemo(() => {
    const formResponseCounts = new Map<string, number>();
    
    items.forEach((response) => {
      if (response.formId) {
        formResponseCounts.set(
          response.formId,
          (formResponseCounts.get(response.formId) || 0) + 1
        );
      }
    });

    return forms.filter((form) => formResponseCounts.has(form.id));
  }, [forms, items]);

  // Establecer la primera revisión con respuestas como seleccionada por defecto
  useEffect(() => {
    if (formsWithResponses.length > 0 && !selectedFormId) {
      setSelectedFormId(formsWithResponses[0].id);
    }
  }, [formsWithResponses, selectedFormId]);

  // Filtrar respuestas por revisión seleccionada
  const filteredResponses = useMemo(() => {
    if (!selectedFormId) return [];
    let rows = items.filter((response) => response.formId === selectedFormId);
    if (belowThresholdOnly) {
      rows = rows.filter((response) => response.belowThreshold);
    }
    return rows;
  }, [items, selectedFormId, belowThresholdOnly]);

  const reloadResponses = () => {
    if (id) {
      dispatch(loadSurveyResponses({ surveyId: id, filters: { ...filters, formId: undefined }, fetchAll: true }));
    }
  };

  // Obtener todas las preguntas únicas para la exportación (solo de la revisión seleccionada)
  const allFieldsForExport = useMemo(() => {
    const fieldMap = new Map<string, FormField>();

    filteredResponses.forEach((response) => {
      if (response.form?.structure?.fields) {
        response.form.structure.fields.forEach((field) => {
          if (!fieldMap.has(field.id)) {
            fieldMap.set(field.id, field);
          }
        });
      }
    });

    return Array.from(fieldMap.values());
    
  }, [filteredResponses]);

  const exportColumns: ExcelColumn<SurveyResponseData>[] = useMemo(() => {
    const columns = allFieldsForExport.map(buildFieldColumn);
    columns.push(buildTotalScoreColumn());
    return columns;
  }, [allFieldsForExport]);

  // Generar nombre de archivo para exportación
  const exportFilename = useMemo(() => {
    const surveyTitle = currentSurvey?.title || singularLabel;
    const sanitizedTitle = surveyTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const selectedForm = forms.find((f) => f.id === selectedFormId);
    const revisionSuffix = selectedForm ? `_revision_${selectedForm.revision ?? selectedForm.version}` : '';
    return `respuestas_${sanitizedTitle}${revisionSuffix}`;
  }, [currentSurvey, selectedFormId, forms, singularLabel]);


  if (!id) {
    return <div>ID de {singularLabel} no válido</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{getAssessmentResponsesTitle(category)}</h1>
          {currentSurvey && (
            <p className="text-sm text-muted-foreground mt-1">
              {currentSurvey.title}
              {currentSurvey.threshold != null && (
                <span> · Umbral: {currentSurvey.threshold.toFixed(2)}</span>
              )}
            </p>
          )}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(listPath)}
        >
          ← Volver a {listLabel}
        </button>
      </div>

      {/* Tabs de revisiones */}
      {formsWithResponses.length > 0 && (
        <>
          <div className="border-b border-border">
            <nav className="flex gap-4">
              {formsWithResponses.map((form) => {
                const responseCount = items.filter((r) => r.formId === form.id).length;
                return (
                  <button
                    key={form.id}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                      selectedFormId === form.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setSelectedFormId(form.id)}
                  >
                    Revisión {form.revision ?? form.version} ({responseCount})
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Filtros y exportación */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={belowThresholdOnly}
                onChange={(e) => setBelowThresholdOnly(e.target.checked)}
                className="rounded border-input"
              />
              Solo respuestas bajo umbral
            </label>
            {canExportExcel && (
              <ExportToExcel
                data={filteredResponses}
                filename={exportFilename}
                columns={exportColumns}
                disabled={loading || filteredResponses.length === 0}
              />
            )}
          </div>

          {/* Tabla de respuestas */}
          <SurveyResponsesTable
            data={filteredResponses}
            loading={loading}
            surveyId={id}
            onResponseUpdated={reloadResponses}
          />
        </>
      )}

      {/* Mostrar mensaje si no hay respuestas */}
      {formsWithResponses.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          No hay respuestas registradas para esta {singularLabel}
        </div>
      )}
    </div>
  );
}








